import {Badge, Modal, OverlayTrigger, Tooltip} from "react-bootstrap";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import './CustomSizeModal.css';
import inventoryService from "../../services/inventory.service";
import {ToastContainer} from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import Toaster from "../../common/ToastMsg";
import NumericInput from 'react-numeric-input';
import {FaInfoCircle, FaRobot} from "react-icons/fa";

const PartsSearchResultsModal = ({ closeModal, items, clientId, refreshInventory }) => {
    const [autoHide] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [selectedPart, setSelectedPart] = useState(undefined);

    const [qty, setQty] = useState(0);
    const [listPrice, setListPrice] = useState(0);
    const [cost, setCost] = useState(0);
    const [location, setLocation] = useState("");
    const [addLoading, setAddLoading] = useState(false);

    const normalizePrice = (price) => {
        const value = Number(price);
        return Number.isFinite(value) && value > 0 ? value : 0;
    }

    const resultRows = useMemo(() => {
        if (!(items instanceof Array)) {
            return [];
        }

        return items.flatMap((item, itemIndex) => {
            const partsNames = item.partsNames instanceof Array ? item.partsNames : [];
            if (partsNames.length <= 0) {
                return [{
                    ...item,
                    partsName: "",
                    rowKey: `${item.id ?? item.partsNumber}-${itemIndex}-empty`
                }];
            }

            return partsNames.map((partsName, descIndex) => ({
                ...item,
                partsName,
                partsNames: [partsName],
                rowKey: `${item.id ?? item.partsNumber}-${itemIndex}-${descIndex}`
            }));
        });
    }, [items]);

    const selectPart = useCallback(index => {
        const p = resultRows[index];
        if (!p) {
            return;
        }

        setSelectedIndex(index);
        setSelectedPart(p);
        setQty(1);
        setListPrice(normalizePrice(p.price));
        setCost(0);
        setLocation("");
    }, [resultRows])

    const handleCheck = useCallback(r => {
        selectPart(r.target.value * 1);
    }, [selectPart])

    useEffect(() => {
        if (resultRows.length === 1) {
            selectPart(0)
        }
    }, [selectPart, resultRows])

    const handleSubmit = () => {
        if (!selectedPart) {
            Toaster.error({
                title: "No part selected",
                text: "Please select a part before adding it."
            }, {
                position: "top-center",
                containerId: "parts"
            });
            return;
        }
        if (qty <= 0) {
            Toaster.error({
                title: "Invalid quantity",
                text: "Please specify a quantity greater than 0."
            }, {
                position: "top-center",
                containerId: "parts"
            });
            return;
        }

        setAddLoading(true);
        addSelectedPartAsNewInventory();
    }

    const addSelectedPartAsNewInventory = () => {
        const description = getSelectedPartDescription();
        if (!description) {
            setAddLoading(false);
            Toaster.error({
                title: "Missing description",
                text: "This part does not have a usable description."
            }, {
                position: "top-center",
                containerId: "parts"
            });
            return;
        }

        inventoryService.addParts(
            clientId,
            selectedPart.partsNumber,
            description,
            qty,
            listPrice,
            cost,
            location,
            0,
            0,
            selectedPart.aiSuggested === true
        ).then(
            response => {
                setAddLoading(false);
                refreshInventory();
                closeModal("inventory has been updated! The new inventory ID is " + response.data);
            },
            error => {
                setAddLoading(false);
                console.log(error);
                Toaster.error({
                    title: "Failed to add parts !",
                    text: error.response?.data || error.message
                }, {
                    position: "top-center",
                    containerId: "parts"
                });
            }
        );
    }

    const getSelectedPartDescription = () => {
        return selectedPart?.partsName ?? "";
    }

    const renderAiIndicator = (item) => {
        if (!item.aiSuggested) {
            return null;
        }

        return (
            <OverlayTrigger
                trigger={["hover", "focus", "click"]}
                placement="top"
                overlay={
                    <Tooltip id={`ai-parts-tooltip-${item.partsNumber}`}>
                        This part was suggested by AI web search because no catalog match was found. Please verify before adding it to inventory.
                    </Tooltip>
                }
            >
                <button type="button" className="btn btn-link btn-sm p-0 ms-2 align-baseline">
                    <Badge bg="info" text="dark">
                        <FaRobot className="me-1" />
                        AI
                    </Badge>
                    <FaInfoCircle className="ms-1 text-info" />
                </button>
            </OverlayTrigger>
        );
    }

    return (
            <Modal show={true} onHide={closeModal} fullscreen={"md-down"} scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>Select Parts</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ToastContainer containerId={"parts"} autoClose={autoHide} />
                    <div className="row mt-3">
                    <table className="table">
                        <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">PN#</th>
                            <th scope="col">Parts</th>
                            <th scope="col">Price</th>
                        </tr>
                        </thead>
                        <tbody>
                        {resultRows.length > 0 && resultRows.map((item, index) => (
                            <tr
                                key={item.rowKey}
                                className={item.aiSuggested ? "table-info" : ""}
                                onClick={() => selectPart(index)}
                                style={{cursor: "pointer"}}
                            >
                                <td>
                                    <input
                                        type="radio"
                                        value={index}
                                        checked={selectedIndex === index}
                                        onChange={handleCheck}
                                    />
                                </td>
                                <td>
                                    {item.partsNumber}
                                    {renderAiIndicator(item)}
                                </td>
                                <td>
                                    {item.partsName}
                                </td>
                                <td>
                                    {normalizePrice(item.price).toFixed(2)}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                    <form>
                        <div className="mb-3">
                            <label htmlFor="qty" className="form-label">
                                QTY
                            </label>
                            <NumericInput min={0} value={qty} precision={0} className="form-control"
                                      onChange={(vn, vs, _) => setQty(vn)}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="listPrice" className="form-label">
                                List Price
                            </label>
                            <NumericInput min={0} value={listPrice} precision={2} className="form-control"
                                          onChange={(vn, vs, _) => setListPrice(vn)}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="cost" className="form-label">
                                Cost
                            </label>
                            <NumericInput min={0} value={cost} precision={2} className="form-control"
                                          onChange={(vn, vs, _) => setCost(vn)}
                            />
                        </div>
                        <div>
                            <label>Location</label>
                            <textarea className="form-control"
                                      value= {location}
                                      onChange={(event) => setLocation(event.target.value)}
                            />
                        </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Close
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={selectedIndex < 0 || !selectedPart?.partsName || addLoading}
                >
                    {addLoading && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    )}
                    {addLoading ? "Adding..." : "Add Parts"}
                </button>
            </Modal.Footer>
        </Modal>
    );

};

export default PartsSearchResultsModal;
